import type { EditorCanvas } from "replo-utils/lib/misc/canvas";

import React from "react";

import { DebugMenu } from "@agent-ui/features/debug/DebugMenu";
import usePublishedSiteUrls from "@agent-ui/features/home/hooks/usePublishedSiteUrls";
import PageSettingsMenu from "@agent-ui/features/page-renderer/components/navbar/PageSettingsMenu";
import { PublishButton } from "@agent-ui/features/page-renderer/components/navbar/PublishButton";
import { useViewportStore } from "@agent-ui/features/page-renderer/store/viewportStore";
import {
  useEditorStoreActions,
  usePanelView,
} from "@agent-ui/store/editorStore";
import { Button } from "@replo/design-system/components/button/Button";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { CANVAS_SIZES } from "@v2/utils/canvas-size";
import { History, MonitorSmartphone, Share } from "lucide-react";

type Props = {
  isLoading?: boolean;
  pageRoute: string | null;
};

function SitePublishButton() {
  const publishedSiteUrls = usePublishedSiteUrls({
    pageRoutesToUseForPreview: ["/"],
  });
  const publishedSiteUrl = publishedSiteUrls[0];

  const isViewableLive = publishedSiteUrl != null;

  return (
    <PublishButton
      publishedSiteUrl={publishedSiteUrl}
      isViewableLive={isViewableLive}
    />
  );
}

function VersionHistoryButton() {
  const panelView = usePanelView();
  const { setPanelView } = useEditorStoreActions();
  const isActive = panelView === "history";

  return (
    <Tooltip content="Version History" triggerAsChild>
      <Button
        icon
        variant="secondary"
        size="base"
        UNSAFE_className={twMerge(
          isActive &&
            "bg-sky-soft text-sky border-sky-border hover:bg-sky-soft hover:text-sky hover:border-sky-border",
        )}
        onClick={() => setPanelView(isActive ? "chat" : "history")}
      >
        <History className={twMerge(isActive && "text-primary")} />
      </Button>
    </Tooltip>
  );
}

function PagePathBar({ pageRoute }: { pageRoute: string | null }) {
  const { currentViewport, applyPreset } = useViewportStore();
  const [localEdit, setLocalEdit] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const skipBlurCommitRef = React.useRef(false);

  const displayValue = localEdit ?? pageRoute ?? "/";

  const handleViewportToggle = () => {
    const nextViewport: EditorCanvas =
      currentViewport === "mobile" ? "desktop" : "mobile";
    applyPreset(nextViewport);
  };

  const tooltipText = (() => {
    const nextViewport: EditorCanvas =
      currentViewport === "mobile" ? "desktop" : "mobile";
    const nextWidth = CANVAS_SIZES[nextViewport].defaultFrameWidth;
    return `Switch to ${nextViewport} (${nextWidth}px)`;
  })();

  const commitValue = React.useCallback(
    (value: string) => {
      setLocalEdit(null);
      const trimmed = value.trim();
      if (!trimmed || pageRoute === "/" || trimmed === pageRoute) {
        return;
      }

      // TODO (Berkley, 2026-03-10, REPL-24964): Use fast llm to edit page routes
    },
    [pageRoute],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      skipBlurCommitRef.current = true;
      commitValue(displayValue);
      inputRef.current?.blur();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      skipBlurCommitRef.current = true;
      setLocalEdit(null);
      inputRef.current?.blur();
    }
  };

  return (
    <div className="flex items-center gap-1 rounded-full bg-surface pl-1.5 pr-1.5 py-1 overflow-hidden">
      <PageSettingsMenu />
      <input
        ref={inputRef}
        type="text"
        value={displayValue}
        onChange={(e) => setLocalEdit(e.target.value)}
        onFocus={() => setLocalEdit(pageRoute ?? "/")}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          if (skipBlurCommitRef.current) {
            skipBlurCommitRef.current = false;
            return;
          }
          commitValue(displayValue);
        }}
        className="flex-1 min-w-0 bg-transparent text-sm text-default outline-none border-none ring-0 focus:ring-0 focus:outline-none p-0 m-0"
      />
      <Tooltip content={tooltipText} triggerAsChild>
        <Button
          icon
          variant="tertiary"
          size="sm"
          UNSAFE_className="text-default shrink-0 rounded-full"
          onClick={handleViewportToggle}
        >
          <MonitorSmartphone size={14} />
        </Button>
      </Tooltip>
    </div>
  );
}

function PageRenderControls({ isLoading = false, pageRoute }: Props) {
  const renderShareButton = () => {
    if (isLoading) {
      return <Skeleton className="size-8 rounded-lg" />;
    }
    return (
      <Tooltip content="Save page to share" triggerAsChild>
        <Button icon variant="secondary" size="base" disabled>
          <Share />
        </Button>
      </Tooltip>
    );
  };

  return (
    <div className="flex border-b-0.5 border-border w-full px-4 py-3 max-h-16 flex-row items-center gap-3">
      <DebugMenu />

      {/* Path bar: fills remaining space */}
      <div className="flex-1 min-w-0">
        <PagePathBar pageRoute={pageRoute} />
      </div>

      {/* Right: share + divider + history + publish */}
      <div className="flex items-center gap-3 shrink-0">
        {renderShareButton()}
        <div className="h-5 w-px bg-border" />
        <VersionHistoryButton />
        <SitePublishButton />
      </div>
    </div>
  );
}

export default PageRenderControls;
